import type { AgentDraft } from "@nemo/agent-schema";
import { useEffect, useMemo, useState } from "react";
import type { Api, Source } from "../api.js";
import { useI18n } from "../i18n.js";
import { computeChecklist, computeWarnings, type Status } from "./checks.js";
import { PANEL, type ChecklistId } from "./text.js";

const STEP_OF: Partial<Record<ChecklistId, "plan" | "train" | "user-data">> = {
  persona: "plan",
  instructions: "plan",
  sliders: "plan",
  knowledge: "train",
  exact_wording: "train",
  lead_capture: "user-data",
};

const key = (agentId: string) => `nemo.ready.${agentId}`;
function loadTicks(agentId: string): Record<string, boolean> {
  try {
    return JSON.parse(localStorage.getItem(key(agentId)) ?? "{}") as Record<string, boolean>;
  } catch {
    return {};
  }
}
function saveTicks(agentId: string, ticks: Record<string, boolean>): void {
  try {
    localStorage.setItem(key(agentId), JSON.stringify(ticks));
  } catch {
    /* private mode: the ticks last until the page closes */
  }
}

const MARK: Record<Status | "done", string> = { ok: "✓", done: "✓", tip: "!", todo: "○", manual: "○" };

/**
 * "Ready to publish?": ten things that make an agent good, six worked out from the settings and three checked by
 * trying the preview. Advice only: it never blocks Publish, makes no AI call and sends nothing anywhere. The ticks for
 * the three manual items are kept on this device (localStorage) and say so.
 */
export function ReadyPanel({ api, agentId, form, onOpenPreview, onGo }: { api?: Api; agentId: string; form: AgentDraft; onOpenPreview?: () => void; onGo?: (step: "plan" | "train" | "user-data") => void }) {
  const { t, lang } = useI18n();
  const [sources, setSources] = useState<Source[] | null>(null);
  const [ticks, setTicks] = useState<Record<string, boolean>>(() => loadTicks(agentId));
  useEffect(() => setTicks(loadTicks(agentId)), [agentId]);
  useEffect(() => {
    let live = true;
    if (!api) return;
    api.sources(agentId).then((s) => live && setSources(s), () => {});
    return () => {
      live = false;
    };
  }, [api, agentId]);

  const items = useMemo(() => computeChecklist({ form, sources }), [form, sources]);
  const warnings = useMemo(() => computeWarnings({ form, sources }), [form, sources]);
  const copy = (id: ChecklistId) => PANEL.checklist.find((c) => c.id === id)!;
  const done = (id: ChecklistId, s: Status) => (s === "manual" ? !!ticks[id] : s === "ok");
  const ready = items.filter((i) => done(i.id, i.status)).length;
  const tick = (id: ChecklistId, on: boolean) => {
    const next = { ...ticks, [id]: on };
    setTicks(next);
    saveTicks(agentId, next);
  };
  const questions = (id: ChecklistId): string[] => {
    const q = (PANEL.tryIt as Record<string, { en: string | readonly string[]; es: string | readonly string[] }>)[id];
    const v = q?.[lang];
    return v === undefined ? [] : typeof v === "string" ? [v] : [...v];
  };

  return (
    <details className="ready" open>
      <summary>
        <strong>{t("ready.title")}</strong> <span className="hint" aria-live="polite">{t("ready.count", { n: String(ready), total: String(items.length) })}</span>
      </summary>
      <p className="hint">{t("ready.advice")}</p>
      <ul className="checks">
        {items.map((i) => {
          const c = copy(i.id);
          const isDone = done(i.id, i.status);
          const state = isDone ? "done" : i.status;
          const step = STEP_OF[i.id];
          return (
            <li key={i.id} className={`check ${isDone ? "ok" : i.status === "tip" ? "warn" : "info"}`}>
              <span className="mark" aria-hidden="true">{MARK[state]}</span>
              <span className="sr-only">{t(isDone ? "ready.s.done" : i.status === "tip" ? "ready.s.tip" : "ready.s.todo")}: </span>
              <span>{c.label[lang]}</span>
              {!isDone && <p className="hint">{c.fix[lang]}{step && onGo && <> <button type="button" className="linklike" onClick={() => onGo(step)}>{t("ready.go")}</button></>}</p>}
              {i.status === "manual" && (
                <div className="row">
                  {onOpenPreview && <button type="button" className="btn secondary" onClick={onOpenPreview}>{t("ready.try")}</button>}
                  {questions(i.id).map((q) => <code key={q} className="suggest">{q}</code>)}
                  <label className="inline">
                    <input type="checkbox" checked={!!ticks[i.id]} onChange={(e) => tick(i.id, e.target.checked)} /> {t("ready.looksgood")}
                  </label>
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {warnings.length > 0 && (
        <ul className="plain" aria-label={t("ready.warnings")}>
          {warnings.map((w) => <li key={w} className="hint">! {PANEL.warnings[w][lang]}</li>)}
        </ul>
      )}
      <p className="hint">{t("ready.device")}</p>
    </details>
  );
}
